import {deleteCookie, getCookie, getValueFromCookie, setCookie, restoreCookieFromLocalStorage} from '../util/storage'
import {getLocalStorage, setLocalStorage, removeLocalStorage} from '../util/storage'
import {MultiWindowEmitter} from '../util/event'
import {Emitter} from '../util/event'
import {version as cmpVersion} from '../../../package.json'
import debug, {DEBUG, INFO, WARN} from '../util/debug'
import {searchParams} from '../util/location'
import {getConsentData} from '../util/consent-string'
import {ConsentString} from 'consent-string'

import {
  fetchDirectVendorList,
  fetchVendorList,
  fetchVendors,
  VendorList,
  Vendor
} from '../service/vendor-list'

import {
  fetchPublisherPurposesList,
  PublisherPurposesList
} from '../service/publisher-purposes-list'

const getRootWindowDomain = () => {
  const match = ((window.location !== window.parent.location)
    ? document.referrer
    : document.location.href).match(/https?:\/\/([^:\/\n]+)/im)
  return match ? match[1] : undefined
}

export default class ConsentModel extends MultiWindowEmitter {
  public static cookieName = 'euconsent'

  public static async load (baseURL: string): Promise<ConsentModel> {
    const [publisherPurposesList, vendorList] = await Promise.all([
      fetchPublisherPurposesList(baseURL),
      fetchVendorList(baseURL)
    ])
    const consentModel = new this(baseURL, vendorList, publisherPurposesList)
    await consentModel.load()
    return consentModel
  }

  public consented: boolean
  public metadata: string

  // @ts-ignore This gets initialized within .load() which is called within the constructor
  protected consentData: ConsentString

  protected constructor (
    protected baseURL: string,
    public vendorList: VendorList,
    public publisherPurposesList: PublisherPurposesList
  ) {
    super()
    this.metadata = ''
    this.consented = false
    this.on('update', this.load)
    this.on('destroy', this.unload)
    this.load()
  }

  public get daisybit (): string {
    if (this.metadata) {
      return this.metadata
    }

    return getConsentData(this.vendorList).getConsentString()
  }

  public load = () => {
    this.metadata = getCookie(ConsentModel.cookieName)

    if (!this.metadata) {
      // No cookie? Check for a consent token in localStorage and restore
      const lsMetaData = restoreCookieFromLocalStorage(ConsentModel.cookieName)
        && restoreCookieFromLocalStorage('uuid')
        && getLocalStorage(ConsentModel.cookieName)

      if (lsMetaData) {
        this.metadata = lsMetaData
      }
    }

    debug(`loading metadata "${this.metadata}"`, DEBUG)
    this.consentData = new ConsentString(this.metadata)

    this.upgradeConsent() // intentionally not awaiting for upgradeConsent

    this.consented = !!this.consentData.getVendorsAllowed().length
  }

  public unload = () => {
    deleteCookie(ConsentModel.cookieName)
    deleteCookie('uuid')
    removeLocalStorage(ConsentModel.cookieName)
    removeLocalStorage(`${ConsentModel.cookieName}-expires`)
    removeLocalStorage('uuid')
    removeLocalStorage('uuid-expires')
  }

  public hasChoiceBeenMade (): boolean {
    return !!this.metadata
  }

  public getAllVendorIds (): string[] {
    return this.vendorList.vendors.map(({id}) => id.toString())
  }

  public getConsentVersion (): number {
    return this.consentData.getVersion()
  }

  public getConsentData (version?: number) {
    if (typeof version !== 'undefined' && version !== this.consentData.getVersion()) {
      return null
    }

    return this.metadata
  }

  public getCookieExpireDays (consented: boolean): number {
    const consentExpires = 13 * 30 // 13 months
    const noConsentExpires = 30 // 1 month
    return consented ? consentExpires : noConsentExpires
  }

  public updateStorage (euconsent: string, uuid: string): void {
    const euExdays = this.getCookieExpireDays(this.consented)
    const euDate = new Date()
    euDate.setTime(euDate.getTime() + (euExdays * 24 * 60 * 60 * 1000))

    setLocalStorage(ConsentModel.cookieName, euconsent)
    setLocalStorage(`${ConsentModel.cookieName}-expires`, euDate.toUTCString())

    const uuidExDays = 100 * 365 // 100 years
    const uuidDate = new Date()
    uuidDate.setTime(uuidDate.getTime() + (uuidExDays * 24 * 60 * 60 * 1000))

    setLocalStorage('uuid', uuid)
    setLocalStorage(`uuid-expires`, uuidDate.toUTCString())
  }

  public async save (origin: string): Promise<void> {
    const res = await this.send(origin)
    const {euconsent, uuid} = await res.json()
    this.updateStorage(euconsent, uuid)

    this.load()
    this.emit('update')
  }

  public destroy () {
    deleteCookie(ConsentModel.cookieName)
    deleteCookie('uuid')
    removeLocalStorage(ConsentModel.cookieName)
    removeLocalStorage(`${ConsentModel.cookieName}-expires`)
    removeLocalStorage('uuid')
    removeLocalStorage('uuid-expires')
    this.emit('destroy')
  }

  protected send (origin: string, channel?: any): Promise<Response> {
    return fetch(`${this.baseURL}/../gdpr/consent`, {
      body: JSON.stringify({
        channel: searchParams['mol.ads.cmp.channel'] || channel || null,
        consent: this.consented ? 'yes' : 'no',
        domain: origin
      }),
      credentials: 'include',
      headers: {
        'content-type': 'application/json'
      },
      method: 'POST'
    })
  }
  private async postUpgrade (bodyOptions: object ) {
   const rp  = await fetch(`${this.baseURL}/../gdpr/consent/upgrade`, {body: JSON.stringify(bodyOptions),
      credentials: 'include',
      headers: {
        'content-type': 'application/json'
      },
      method: 'POST'})
  }

  private async upgradeConsent () {
    const bodyOptions = []
    if (this.consentData.getCmpId() === 1) {
      debug('added item to post for cmpId')

      let chance = parseFloat(localStorage.getItem('cmp-chance-of-upgrade') || '-1')
      if (chance === -1) {
        chance = Math.random()
        localStorage.setItem('cmp-chance-of-upgrade', '' + chance)
      }

      const rollOutStart = Date.parse('2019-01-24 10:00:00')
      const rollOutComplete = Date.parse('2019-01-25 16:00:00')
      const rollOutProgress = (+new Date() - rollOutStart) / (rollOutComplete - rollOutStart)
      if (chance <= rollOutProgress) {
        bodyOptions.push({})
      }

    }
    if (this.consentData.getVendorListVersion() !== this.vendorList.vendorListVersion) {
      const MISSING_FROM_VERSION_60 = [83, 124, 171, 191, 216, 223, 239, 250, 251, 252, 262, 263, 266, 270, 281, 282,
        285, 288, 289, 290, 291, 297, 301, 303, 310, 311, 314, 318, 319, 326, 329, 330, 331, 337, 343, 344, 347, 354,
        359, 360, 361, 368, 376, 377, 378, 380, 389, 390, 391, 398, 400, 402, 403, 404, 405, 421, 422, 426, 435]

      const VENDORS_EVIDON_AND_IAB_JUNE_2018 = [31, 44, 64, 65, 84, 87, 92, 104, 115, 119, 125, 126, 141, 142, 148, 150, 155, 156, 160, 162,
        165, 167, 169, 170, 174, 177, 178, 179, 182, 184, 185, 188, 189, 190, 192, 193, 194, 195, 197, 199, 200, 201,
        202, 203, 205, 208, 209, 210, 211, 213, 215, 218, 224, 225, 226, 227, 228, 229, 230, 231, 232, 234, 235,
        236, 238, 240, 241, 242, 244, 245, 246, 248, 249, 253, 254, 255, 256, 257, 258, 259, 260, 264, 265,
        269, 273, 274, 276, 278, 279, 280, 284, 287, 294, 295, 299, 308, 309, 312, 316, 317, 320, 323,
        325, 328, 333, 351, 369, 371, 373, 382, 385, 388, 394, 413, 434]

      const vendorIds = this.consentData.getVendorsAllowed()

      // this is to remove SpotX and Adobe from users that dissented somehow ended up with both due to previous fix
      const wasDissentingUsersVendorListIncorrectlyFixed = () => vendorIds.length === 2

      if (wasDissentingUsersVendorListIncorrectlyFixed() ||
        !vendorIds.every((id: number) => VENDORS_EVIDON_AND_IAB_JUNE_2018.includes(id)) ||
        vendorIds.some((id: number) => MISSING_FROM_VERSION_60.includes(id))) {

        let chance = parseFloat(localStorage.getItem('cmp-chance-of-upgrade') || '-1')
        if (chance === -1) {
          chance = Math.random()
          localStorage.setItem('cmp-chance-of-upgrade', '' + chance)
        }

        const rollOutStart = Date.parse('2018-08-08 09:35:00')
        const rollOutComplete = Date.parse('2018-08-09 16:00:00')
        const rollOutProgress = (+new Date() - rollOutStart) / (rollOutComplete - rollOutStart)

        if (chance <= rollOutProgress) {
          const newVendorIds = wasDissentingUsersVendorListIncorrectlyFixed() ? [] : [
            ...new Set([
              ...vendorIds.filter((id: number) => !MISSING_FROM_VERSION_60.includes(id)),
              ...VENDORS_EVIDON_AND_IAB_JUNE_2018])
          ].sort((a, b) => a - b)
          bodyOptions.push({vendorIds: newVendorIds})
        } else {
          debug(`Not updating outdated vendor list yet (${chance} > ${rollOutProgress})`, INFO)
        }
      }
    }
    if (bodyOptions.length === 0) {
      return
    }
    try {
        const body = {
          domain: getRootWindowDomain(),
          ...bodyOptions.reduce((acc, next) => ({...acc, ...next}), {})
        }
        await this.postUpgrade(body)
        const metadata = getCookie(ConsentModel.cookieName)
        const consentData = new ConsentString(metadata)
        debug(`New vendor list version: ${consentData.getVendorListVersion()}`, INFO)
      } catch (e) {
        debug(`Error trying to upgrade consent: ${e}`, WARN)
      }
  }
}
